"use client";
import { IBlocks, IBlog, IBlogPost, IUserData } from "@/types";
import React from "react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { IconFilter, IconSearch } from "@tabler/icons-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import AdminPopover from "./utils/AdminPopover";
import { BlogPostSkeletonPaginate } from "@/components/admin/blog/skeleton";
import PaginateBlog from "@/components/admin/blog/paginate";
import type { BlockUpdateFunction } from "./utils/blocks-map";
import {
    BORDER_RADIUS_OPTIONS,
    SHADOW_OPTIONS,
} from "./utils/block-style-classes";
interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    theme: IUserData["chosen_theme"];
}
const itemsPerPage = 5;
export default function BlockBlogPostAll({
    edit,
    account,
    block,
    update,
    theme,
}: Props) {
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [blog, setBlog] = React.useState<IBlog | null>(null);
    const [posts, setPosts] = React.useState<{
        posts: IBlogPost[];
        totalPosts: number;
        currentPage: number;
        totalPages: number;
    } | null>(null);
    const [categories, setCategories] = React.useState(blog?.categories || []);
    const [search, setSearch] = React.useState("");
    const [selectedCategories, setSelectedCategories] = React.useState<
        string[]
    >([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [isLoading, setIsLoading] = React.useState(true);
    async function getPosts() {
        if (
            blockUpdate?.options?.category &&
            blockUpdate?.options?.category !== "all"
        ) {
            const res = await fetch(
                `/api/account/${account.id}/blog?page=1&limit=${itemsPerPage}${`&categories=["${blockUpdate.options.category}"]`}`
            );
            const data = await res.json();
            if (data.data) {
                setPosts(data.data);
                setBlog(data.data.blog);
                setCategories(data.data.blog.categories);
                setIsLoading(false);
            }
        } else {
            const res = await fetch(
                `/api/account/${account.id}/blog?page=1&limit=${itemsPerPage}${selectedCategories && selectedCategories?.length > 0 ? `&categories=[${selectedCategories}]` : ""}`
            );
            const data = await res.json();
            if (data.data) {
                setPosts(data.data);
                setBlog(data.data.blog);
                setCategories(data.data.blog.categories);
                setIsLoading(false);
            }
        }
    }
    React.useEffect(() => {
        getPosts();
    }, [blockUpdate]);
    React.useEffect(() => {
        getPosts();
        setCurrentPage(1);
    }, [selectedCategories]);
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={"Blog Post All"}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            <div
                key={block.id}
                className={cn(
                    `relative flex w-full flex-row flex-wrap gap-2 p-2`,
                    BORDER_RADIUS_OPTIONS[theme.profile.design.corner_radius]
                        ?.class,
                    SHADOW_OPTIONS[theme.profile.design.shadow]?.class,
                    "block-" + block.id
                )}
                id="BlogPostSingle"
            >
                {isLoading && <BlogPostSkeletonPaginate account={account} />}
                {blog && posts && (
                    <>
                        <div className="line-clamp-1 flex w-full items-center justify-between gap-2 pb-2">
                            <p className="text-lg font-bold text-user-primary-text-color">
                                {blockUpdate.name}
                            </p>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        hidden={
                                            blockUpdate?.options?.category !==
                                                "all" &&
                                            blockUpdate?.options?.category !==
                                                undefined &&
                                            blockUpdate?.options?.category !==
                                                null
                                        }
                                        id="block"
                                        className={cn(
                                            `relative  w-fit p-1 text-xl transition-all duration-150 hover:scale-105`,
                                            SHADOW_OPTIONS[
                                                theme.profile.design.shadow
                                            ]?.class,
                                            BORDER_RADIUS_OPTIONS[
                                                theme.profile.design
                                                    .corner_radius
                                            ]?.class
                                        )}
                                        style={{
                                            backgroundColor:
                                                theme.profile.colors.primary,
                                            color: theme.profile.colors
                                                .primary_text,
                                        }}
                                    >
                                        <IconFilter />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className={cn(
                                        `max-h-64 w-full max-w-full overflow-auto bg-user-secondary-color p-2`,
                                        SHADOW_OPTIONS[
                                            theme.profile.design.shadow
                                        ]?.class,
                                        BORDER_RADIUS_OPTIONS[
                                            theme.profile.design.corner_radius
                                        ]?.class
                                    )}
                                >
                                    <div className="flex flex-col gap-3">
                                        <div
                                            className={cn(
                                                `group flex items-center gap-2 border border-transparent p-1 transition-all duration-300 hover:border-user-primary-color`,
                                                SHADOW_OPTIONS[
                                                    theme.profile.design.shadow
                                                ]?.class,
                                                BORDER_RADIUS_OPTIONS[
                                                    theme.profile.design
                                                        .corner_radius
                                                ]?.class
                                            )}
                                        >
                                            <Input
                                                variant={"card"}
                                                placeholder="Search categories..."
                                                value={search}
                                                className={cn(
                                                    `border-0 !bg-transparent text-user-primary-text-color placeholder:text-opacity-50 focus-visible:border-0`,
                                                    SHADOW_OPTIONS[
                                                        theme.profile.design
                                                            .shadow
                                                    ]?.class,
                                                    BORDER_RADIUS_OPTIONS[
                                                        theme.profile.design
                                                            .corner_radius
                                                    ]?.class
                                                )}
                                                style={{
                                                    color: theme.profile.colors
                                                        .secondary_text,
                                                }}
                                                onChange={(e) => {
                                                    setSearch(e.target.value);
                                                    setCategories(
                                                        blog.categories.filter(
                                                            (category) => {
                                                                return category.name
                                                                    .toLowerCase()
                                                                    .includes(
                                                                        e.target.value.toLowerCase()
                                                                    );
                                                            }
                                                        )
                                                    );
                                                }}
                                            />
                                            <Button
                                                className={cn(
                                                    `bg-transparent hover:bg-user-primary-color`,
                                                    SHADOW_OPTIONS[
                                                        theme.profile.design
                                                            .shadow
                                                    ]?.class,
                                                    BORDER_RADIUS_OPTIONS[
                                                        theme.profile.design
                                                            .corner_radius
                                                    ]?.class
                                                )}
                                            >
                                                <IconSearch />
                                            </Button>
                                        </div>
                                        <Separator
                                            className="opacity-50"
                                            style={{
                                                background:
                                                    theme.profile.colors
                                                        .primary,
                                            }}
                                        />
                                        {categories.map((category) => (
                                            <div
                                                className="flex items-center space-x-2"
                                                key={category.id}
                                            >
                                                <Checkbox
                                                    className={`border-user-primary-color data-[state=checked]:border-user-primary-color data-[state=checked]:bg-user-primary-color`}
                                                    style={{
                                                        color: theme.profile
                                                            .colors
                                                            .primary_text,
                                                    }}
                                                    id={
                                                        "category-" +
                                                        category.id
                                                    }
                                                    onCheckedChange={(
                                                        checked
                                                    ) => {
                                                        if (checked) {
                                                            setSelectedCategories(
                                                                (prev) => [
                                                                    ...prev,
                                                                    `"${category.id}"`,
                                                                ]
                                                            );
                                                        } else {
                                                            setSelectedCategories(
                                                                (prev) =>
                                                                    prev.filter(
                                                                        (
                                                                            _category
                                                                        ) =>
                                                                            _category !==
                                                                            `"${category.id}"`
                                                                    )
                                                            );
                                                        }
                                                    }}
                                                    checked={selectedCategories.includes(
                                                        `"${category.id}"`
                                                    )}
                                                />
                                                <label
                                                    htmlFor={
                                                        "category-" +
                                                        category.id
                                                    }
                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                    style={{
                                                        color: theme.profile
                                                            .colors
                                                            .primary_text,
                                                    }}
                                                >
                                                    {category.name}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                        <PaginateBlog
                            account={account}
                            blog={blog}
                            posts={posts.posts || []}
                            itemsPerPage={itemsPerPage}
                            totalItems={posts.totalPosts || 0}
                            categories={
                                (blockUpdate.options?.category &&
                                    blockUpdate.options?.category !== "all" && [
                                        `"${blockUpdate.options?.category}"`,
                                    ]) ||
                                selectedCategories
                            }
                            pageNumber={currentPage}
                            accountTheme={{
                                ...account.theme,
                                text_col_button:
                                    theme.profile.colors.primary_text,
                                button: theme.profile.colors.primary,
                            }}
                            v2Theme={theme.profile as any}
                        />
                    </>
                )}
            </div>
        </AdminPopover>
    );
}
